import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { buildCanonical } from "@/lib/canonical";

/**
 * Global fallback that guarantees every route has a correct canonical tag.
 * Pages using usePageMeta set their own canonical and mark it with
 * data-canonical-source="page"; this manager only writes a default when a
 * page hasn't already claimed the canonical for the current path.
 *
 * React fires child effects before parent effects, so on a usePageMeta route
 * the page hook runs first and this manager then defers to it — ensuring a
 * single, correct canonical with no override or duplication.
 */
const CanonicalManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

    // A page already set the canonical for this exact path — leave it.
    if (
      link &&
      link.dataset.canonicalSource === "page" &&
      link.dataset.canonicalPath === pathname
    ) {
      return;
    }

    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }

    link.setAttribute("href", buildCanonical(pathname));
    link.dataset.canonicalSource = "default";
    link.dataset.canonicalPath = pathname;
  }, [pathname]);

  return null;
};

export default CanonicalManager;
